// ============================================================================
// formUtils.tsx
// Chart configuration and data transformation utilities for behavior analysis
// ============================================================================

import * as Yup from "yup";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  PointElement,
  LineElement,
  Title,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  Tooltip,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Legend,
  ChartDataLabels
);

// ============================================================================
// Validation Schema (if needed for forms)
// ============================================================================
export const behaviorAnalysisValidator = () => {
  return Yup.object({
    facilityInfo: Yup.object().shape({
      value: Yup.string().required("Facility is required"),
    }),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date().required("End date is required"),
  });
};

// ============================================================================
// Behavior Types - Complete list of behavior incident categories
// ============================================================================
export const BEHAVIOR_TYPES = {
  AGGRESSION: "Aggression",
  PHYSICAL: "Physical",
  VERBAL: "Verbal",
  SEXUAL: "Sexual Behavior",
  ELOPEMENT: "Elopement",
  WANDERING: "Wandering",
  SELF_HARM: "Self-Harm",
  REFUSAL_OF_CARE: "Refusal of Care",
  PROPERTY_DAMAGE: "Property Damage",
  OTHER: "Other",
} as const;

export type BehaviorType = (typeof BEHAVIOR_TYPES)[keyof typeof BEHAVIOR_TYPES];

// ============================================================================
// Target of Aggression Types - Who was the target of the behavior
// ============================================================================
export const TARGET_OF_AGGRESSION_TYPES = {
  STAFF: "Staff",
  RESIDENT: "Resident",
  VISITOR: "Visitor",
  SELF: "Self",
  PROPERTY: "Property",
  FAMILY: "Family",
  OTHER: "Other",
} as const;

export type TargetOfAggressionType =
  (typeof TARGET_OF_AGGRESSION_TYPES)[keyof typeof TARGET_OF_AGGRESSION_TYPES];

// ============================================================================
// Chart Colors - Consistent colors for behavior types and targets
// ============================================================================
export const BEHAVIOR_TYPE_COLORS: Record<string, string> = {
  [BEHAVIOR_TYPES.AGGRESSION]: "#FF6384",
  [BEHAVIOR_TYPES.PHYSICAL]: "#FF6384",
  [BEHAVIOR_TYPES.VERBAL]: "#36A2EB",
  [BEHAVIOR_TYPES.SEXUAL]: "#FFCE56",
  [BEHAVIOR_TYPES.ELOPEMENT]: "#4BC0C0",
  [BEHAVIOR_TYPES.WANDERING]: "#9966FF",
  [BEHAVIOR_TYPES.SELF_HARM]: "#FF9F40",
  [BEHAVIOR_TYPES.REFUSAL_OF_CARE]: "#C9CBCF",
  [BEHAVIOR_TYPES.PROPERTY_DAMAGE]: "#7C4DFF",
  [BEHAVIOR_TYPES.OTHER]: "#607D8B",
};

export const TARGET_OF_AGGRESSION_COLORS: Record<string, string> = {
  [TARGET_OF_AGGRESSION_TYPES.STAFF]: "#FF6384",
  [TARGET_OF_AGGRESSION_TYPES.RESIDENT]: "#36A2EB",
  [TARGET_OF_AGGRESSION_TYPES.VISITOR]: "#FFCE56",
  [TARGET_OF_AGGRESSION_TYPES.SELF]: "#4BC0C0",
  [TARGET_OF_AGGRESSION_TYPES.PROPERTY]: "#9966FF",
  [TARGET_OF_AGGRESSION_TYPES.FAMILY]: "#FF9F40",
  [TARGET_OF_AGGRESSION_TYPES.OTHER]: "#607D8B",
};

// ============================================================================
// Column Definitions for Kendo Grid (Incidents by Unit table)
// ============================================================================
export const behaviorIncidentUnitCols: any[] = [
  { Header: "Unit", accessor: "unit" },
  { Header: "Total Incidents", accessor: "totalIncidents" },
  { Header: "Aggression", accessor: "aggression" },
  { Header: "Physical", accessor: "physical" },
  { Header: "Verbal", accessor: "verbal" },
  { Header: "Sexual", accessor: "sexual" },
  { Header: "Elopement", accessor: "elopement" },
  { Header: "Wandering", accessor: "wandering" },
  { Header: "Self-Harm", accessor: "selfHarm" },
  { Header: "Refusal of Care", accessor: "refusalOfCare" },
  { Header: "Property Damage", accessor: "propertyDamage" },
  { Header: "Other", accessor: "other" },
];

// ============================================================================
// Column Definitions for Target of Aggression Breakdown
// ============================================================================
export const targetOfAggressionCols: any[] = [
  { Header: "Target", accessor: "target" },
  { Header: "# of Incidents", accessor: "count" },
  { Header: "%", accessor: "percentage" },
];

// ============================================================================
// Line Chart Options - For BehaviorIncidentTrend component
// ============================================================================
export const lineOptions = {
  responsive: true,
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  stacked: false,
  plugins: {
    tooltip: {
      enabled: true,
      intersect: false,
    },
    legend: {
      position: "top" as const,
    },
    datalabels: {
      display: true,
      color: "black",
      formatter: (value: any) => handleFormatter(value),
      font: {
        size: 16,
      },
    },
  },
  scales: {
    y: {
      ticks: {
        display: false,
      },
    },
  },
  onHover: (e: any, chart: any) => {
    e.native.target.style.cursor = chart[0] ? "pointer" : "default";
  },
};

// ============================================================================
// Value Formatter - Hides zero values on charts
// ============================================================================
export const handleFormatter = (value: string | number) => {
  if (Number(value) > 0) return value;
  return null;
};

// ============================================================================
// Bar Chart Options (Vertical) - With click handler for drill-down
// ============================================================================
export const barOptions = (fn: Function) => {
  return {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: "top" as const,
      },
      datalabels: {
        display: true,
        color: "black",
        formatter: (value: any) => handleFormatter(value),
        font: {
          size: 16,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          display: false,
        },
      },
    },
    onClick: (e: any, chart: any) => {
      fn(chart);
    },
    onHover: (e: any, chart: any) => {
      e.native.target.style.cursor = chart[0] ? "pointer" : "default";
    },
  };
};

// ============================================================================
// Bar Chart Options (Horizontal) - For sections that need horizontal bars
// ============================================================================
export const barOptionsHorizontal = (fn: Function) => {
  return {
    indexAxis: "y" as const,
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: "right" as const,
      },
      datalabels: {
        display: true,
        color: "black",
        formatter: (value: any) => handleFormatter(value),
        font: {
          size: 16,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          display: false,
        },
      },
    },
    onClick: (e: any, chart: any) => {
      fn(chart);
    },
    onHover: (e: any, chart: any) => {
      e.native.target.style.cursor = chart[0] ? "pointer" : "default";
    },
  };
};

// ============================================================================
// Handle Point Clicked - Extracts label from clicked chart element
// ============================================================================
export const handlePointClicked = (chart: any, labels: any) => {
  const index = chart[0].index;
  const label = labels[index];
  return label;
};

// ============================================================================
// Transform Data for Bar Chart
// Converts table data array into Chart.js format
// ============================================================================
interface DataItem {
  [key: string]: any;
  count: number;
}

interface BarChartDataset {
  label: string;
  backgroundColor: string;
  borderWidth: number;
  data: number[];
}

interface BarChartData {
  labels: string[];
  datasets: BarChartDataset[];
}

export const transformDataForBarChart = (
  data: DataItem[],
  labelField: string,
  valueField: string = "count"
): BarChartData => {
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const labelCountMap: Record<string, number> = {};

  // Process each data item, filtering out "Total" rows
  data.forEach((item) => {
    const labelValue = item[labelField];
    
    // Skip rows where the label field contains "Total"
    if (!labelValue || String(labelValue).includes("Total")) {
      return;
    }

    // Aggregate counts for the same label
    if (labelCountMap[labelValue]) {
      labelCountMap[labelValue] += item[valueField] || 0;
    } else {
      labelCountMap[labelValue] = item[valueField] || 0;
    }
  });

  // Extract labels and counts
  const labels = Object.keys(labelCountMap);
  const counts = Object.values(labelCountMap);

  // Sort by day-of-week if applicable
  if (labels.some((label) => daysOfWeek.includes(label))) {
    const sortedLabels = labels
      .filter((label) => daysOfWeek.includes(label))
      .sort((a, b) => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b));

    const sortedCounts = sortedLabels.map((label) => labelCountMap[label]);

    return {
      labels: sortedLabels,
      datasets: [
        {
          label: "Count",
          backgroundColor: "#b580ff",
          borderWidth: 1,
          data: sortedCounts,
        },
      ],
    };
  }

  return {
    labels,
    datasets: [
      {
        label: "Count",
        backgroundColor: "#b580ff",
        borderWidth: 1,
        data: counts,
      },
    ],
  };
};

// ============================================================================
// Helper: Convert camelCase to Title Case
// ============================================================================
export function convertToTitleCase(inputString: string) {
  return inputString
    .replace("ByMonth", "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
}

// ============================================================================
// Helper: Check if property is valid object
// ============================================================================
export function isValidObject(property: any) {
  return (
    property !== null &&
    typeof property === "object" &&
    !Array.isArray(property) &&
    Object.keys(property).length > 0
  );
}

// ============================================================================
// Get Metrics - Extract non-total metrics from object for display
// ============================================================================
export function getMetrics(obj: any) {
  const result: any[] = [];

  for (const key in obj) {
    if (obj.hasOwnProperty(key) && !key.includes("total")) {
      const value = obj[key];
      if (typeof value !== "object" || value === null) {
        result.push({ title: convertToTitleCase(key), value });
      }
    }
  }

  return result;
}

// ============================================================================
// Get Unique Sorted Labels - Extract unique month/period labels from data
// ============================================================================
function getUniqueSortedLabels(obj: any) {
  const uniqueLabels = new Set();
  Object.keys(obj).forEach((metric) => {
    if (isValidObject(obj[metric])) {
      Object.keys(obj[metric]).forEach((month) => {
        uniqueLabels.add(month);
      });
    }
  });
  const result = Array.from(uniqueLabels);
  return result;
}

// ============================================================================
// Convert Object to Chart Data - Transform trend data into Chart.js format
// ============================================================================
export function convertObjectToChartData(obj: any) {
  const datasets: any[] = [];
  const uniqueLabels = getUniqueSortedLabels(obj);
  const labels = sortLabelsByDate(uniqueLabels);

  const dataArrays: any = {};
  for (const metric in obj) {
    if (isValidObject(obj[metric])) {
      dataArrays[metric] = Array(labels.length).fill(0);
    }
  }

  for (const metric in dataArrays) {
    const dataObj = obj[metric];
    for (const month in dataObj) {
      const monthIndex = labels.indexOf(month);
      if (monthIndex !== -1) {
        dataArrays[metric][monthIndex] = dataObj[month];
      }
    }
  }

  const colors = [
    "#FF6384",
    "#36A2EB",
    "#FFCE56",
    "#4BC0C0",
    "#9966FF",
    "#FF9F40",
  ];

  let colorIndex = 0;

  for (const metric in dataArrays) {
    if (dataArrays.hasOwnProperty(metric)) {
      const color = colors[colorIndex % colors.length];
      datasets.push({
        label: formatKnownStrings(metric),
        data: dataArrays[metric],
        fill: false,
        borderColor: color,
        pointBorderWidth: 5,
        pointRadius: 10,
        pointHoverRadius: 15,
        tension: 0.4,
        pointHoverBorderWidth: 5,
        yAxisID: "y",
      });
      colorIndex++;
    }
  }

  return { labels, datasets };
}

// ============================================================================
// Sort Labels By Date - Sort month/period labels chronologically
// ============================================================================
export function sortLabelsByDate(labels: any) {
  return labels.sort((a: string, b: string) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA.getTime() - dateB.getTime();
  });
}

// ============================================================================
// Format Known Strings - Convert metric names to display-friendly format
// Maps behavior-specific metric keys to readable labels
// ============================================================================
export function formatKnownStrings(str: string) {
  const knownMappings: Record<string, string> = {
    // Behavior Types - Monthly trends
    aggressionByMonth: "Aggression",
    physicalByMonth: "Physical",
    verbalByMonth: "Verbal",
    sexualByMonth: "Sexual Behavior",
    elopementByMonth: "Elopement",
    wanderingByMonth: "Wandering",
    selfHarmByMonth: "Self-Harm",
    refusalOfCareByMonth: "Refusal of Care",
    propertyDamageByMonth: "Property Damage",
    otherByMonth: "Other",
    totalByMonth: "Total Incidents",

    // Behavior Types - Direct keys
    aggression: "Aggression",
    physical: "Physical",
    verbal: "Verbal",
    sexual: "Sexual Behavior",
    elopement: "Elopement",
    wandering: "Wandering",
    selfHarm: "Self-Harm",
    refusalOfCare: "Refusal of Care",
    propertyDamage: "Property Damage",
    other: "Other",

    // Target of Aggression - Monthly trends
    staffByMonth: "Staff",
    residentByMonth: "Resident",
    visitorByMonth: "Visitor",
    selfByMonth: "Self",
    propertyByMonth: "Property",
    familyByMonth: "Family",

    // Target of Aggression - Direct keys
    staff: "Staff",
    resident: "Resident",
    visitor: "Visitor",
    self: "Self",
    property: "Property",
    family: "Family",
  };
  return knownMappings[str] || convertToTitleCase(str);
}